import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useCall } from "@/api/commerce";
import { useMe } from "@/api/hooks";
import { useAssets, useContract, useContracts, type Contract } from "@/api/service";
import { ColumnChart } from "@/components/charts/charts";
import { Badge } from "@/components/crm/Badge";
import { RecordPicker, type PickerValue } from "@/components/crm/RecordPicker";
import { Icon } from "@/components/icons";
import { Alert, EmptyState, Modal, PageHeader, Spinner, formatDate } from "@/components/ui";
import { money, shortDate } from "@/lib/format";
import { can } from "@/lib/permissions";
import { displayPrefs } from "@/lib/time";
import { StatusBadge, errorText } from "./common";

const AMC_STATUS = { active: ["Active", "ok"], expired: ["Expired", "danger"], renewed: ["Renewed", "neutral"], cancelled: ["Cancelled", "neutral"] } as const;
const today = () => new Date().toLocaleDateString("en-CA");

function NewContract({ onClose, onCreated, account }: { onClose: () => void; onCreated: (c: Contract) => void; account?: PickerValue }) {
  const save = useCall<Contract>("amc", "assets", "tickets");
  const [customer, setCustomer] = useState<PickerValue | null>(account ?? null);
  const machines = useAssets({ account_id: customer?.id }, !!customer);
  const [picked, setPicked] = useState<string[]>([]);
  const [v, setV] = useState({ starts_on: today(), ends_on: "", value: "", visits_per_year: "2" });
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement>) => setV({ ...v, [k]: e.target.value });
  return (
    <Modal title="New AMC contract" onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => {
        e.preventDefault(); setError(null);
        save.mutateAsync({ path: "/amc", body: { account_id: customer?.id, asset_ids: picked, starts_on: v.starts_on, ends_on: v.ends_on || null, value: v.value, visits_per_year: Number(v.visits_per_year) } })
          .then(onCreated).catch((err) => setError(errorText(err)));
      }}>
        {error && <Alert>{error}</Alert>}
        {!account && <RecordPicker label="Customer" endpoint="/accounts" value={customer} onChange={(c) => { setCustomer(c); setPicked([]); }} />}
        {customer && <fieldset><legend className="label">Machines covered</legend>
          {!machines.data?.length ? <p className="text-sm text-muted">This customer has no machines recorded yet.</p> : (
            <div className="grid gap-1 sm:grid-cols-2">{machines.data.map((m) => (
              <label key={m.id} className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-brand" checked={picked.includes(m.id)}
                onChange={(e) => setPicked(e.target.checked ? [...picked, m.id] : picked.filter((x) => x !== m.id))} />{m.name} <span className="font-mono text-xs text-muted">{m.serial_number}</span></label>
            ))}</div>)}
        </fieldset>}
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label htmlFor="amc-start" className="label">Starts on</label><input id="amc-start" className="input" type="date" value={v.starts_on} onChange={set("starts_on")} /></div>
          <div><label htmlFor="amc-end" className="label">Ends on</label><input id="amc-end" className="input" type="date" value={v.ends_on} onChange={set("ends_on")} /><p className="mt-1 text-xs text-muted">Blank: one year.</p></div>
          <div><label htmlFor="amc-value" className="label">Contract value</label><input id="amc-value" className="input" inputMode="decimal" value={v.value} onChange={set("value")} /></div>
          <div><label htmlFor="amc-visits" className="label">Preventive visits a year</label><input id="amc-visits" className="input" type="number" min={0} max={52} value={v.visits_per_year} onChange={set("visits_per_year")} /></div>
        </div>
        <div className="flex justify-end gap-2"><button type="button" className="btn-ghost" onClick={onClose}>Cancel</button><button type="submit" className="btn-primary" disabled={!customer || !v.value || save.isPending}>Create contract</button></div>
      </form>
    </Modal>
  );
}

export function ContractsPage() {
  const navigate = useNavigate();
  const perms = useMe().data?.permissions ?? [];
  const [tab, setTab] = useState<"active" | "due" | "expired" | "all">("active");
  const [creating, setCreating] = useState(false);
  const contracts = useContracts(tab === "due" ? { renewal_due: true } : tab === "all" ? {} : { status: tab });
  return (
    <>
      <PageHeader title="AMC contracts" subtitle="Maintenance contracts with planned visits. You are reminded 60, 30 and 7 days before each one ends."
        actions={can(perms, "amc:manage") && <button type="button" className="btn-primary" onClick={() => setCreating(true)}><Icon name="plus" />New contract</button>} />
      <div className="mb-4 inline-flex rounded-md border border-line bg-surface p-0.5" role="tablist" aria-label="Contracts">
        {([["active", "Active"], ["due", "Renewal due"], ["expired", "Expired"], ["all", "All"]] as const).map(([v, label]) => (
          <button key={v} type="button" role="tab" aria-selected={tab === v} onClick={() => setTab(v)} className={`rounded px-3 py-1 text-sm font-medium ${tab === v ? "bg-brand text-white" : "text-muted hover:text-ink"}`}>{label}</button>))}
      </div>
      {contracts.isLoading ? <Spinner /> : !contracts.data?.length ? <EmptyState title="No contracts here" body="Create a contract for a customer's machines; the visit plan is made for you." /> : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-muted"><tr><th className="px-4 py-2">Contract</th><th className="px-3 py-2">Customer</th><th className="px-3 py-2">Period</th><th className="px-3 py-2 text-right">Value</th><th className="px-3 py-2">Machines</th><th className="px-3 py-2">Status</th></tr></thead>
            <tbody className="divide-y divide-line">
              {contracts.data.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-canvas" onClick={() => navigate(`/amc/${c.id}`)} aria-label={c.number}>
                  <td className="px-4 py-2 font-medium"><Link to={`/amc/${c.id}`} onClick={(e) => e.stopPropagation()} className="hover:text-brand">{c.number}</Link></td>
                  <td className="px-3 py-2">{c.account.name}</td>
                  <td className="px-3 py-2 text-muted">{shortDate(c.starts_on)} – {shortDate(c.ends_on)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(c.value, c.currency)}</td>
                  <td className="px-3 py-2">{c.assets.length}</td>
                  <td className="px-3 py-2"><Badge tone={AMC_STATUS[c.status][1]}>{AMC_STATUS[c.status][0]}</Badge>
                    {c.renewal_due && <span className="ml-2 text-xs font-medium text-warn">Ends in {c.days_left} days</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && <NewContract onClose={() => setCreating(false)} onCreated={(c) => { setCreating(false); navigate(`/amc/${c.id}`); }} />}
    </>
  );
}

function RenewDialog({ c, mode, onClose }: { c: Contract; mode: "quote" | "renew"; onClose: () => void }) {
  const navigate = useNavigate();
  const save = useCall<Contract | { quote_id: string }>("amc", "amc-contract", "quotes");
  const [uplift, setUplift] = useState("0");
  const [error, setError] = useState<string | null>(null);
  const next = Math.round(c.value * (1 + Number(uplift || 0) / 100));
  return (
    <Modal title={mode === "quote" ? "Send a renewal quote" : "Renew now"} onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => {
        e.preventDefault(); setError(null);
        save.mutateAsync({ path: `/amc/${c.id}/${mode === "quote" ? "renewal-quote" : "renew"}`, body: { uplift_pct: uplift || "0" } })
          .then((r) => navigate("quote_id" in r ? `/quotes/${r.quote_id}` : `/amc/${r.id}`)).then(onClose).catch((err) => setError(errorText(err)));
      }}>
        {error && <Alert>{error}</Alert>}
        <p className="text-sm text-muted">{mode === "quote" ? "A draft quote for the next year, ready to send to the customer." : "Creates the next contract straight away, from the day after this one ends, with the same machines and visits."}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label htmlFor="rn-uplift" className="label">Price change (%)</label><input id="rn-uplift" className="input" inputMode="decimal" value={uplift} onChange={(e) => setUplift(e.target.value)} /></div>
          <div><p className="label">New value</p><p className="py-2 text-lg font-semibold tabular-nums">{money(next, c.currency)}</p></div>
        </div>
        <div className="flex justify-end gap-2"><button type="button" className="btn-ghost" onClick={onClose}>Cancel</button><button type="submit" className="btn-primary" disabled={save.isPending}>{mode === "quote" ? "Make quote" : "Renew"}</button></div>
      </form>
    </Modal>
  );
}

export function ContractPage() {
  const { id } = useParams();
  const contract = useContract(id);
  const perms = useMe().data?.permissions ?? [];
  const [dialog, setDialog] = useState<"quote" | "renew" | null>(null);
  if (contract.isLoading) return <Spinner />;
  if (contract.error) return <Alert>{contract.error.message}</Alert>;
  const c = contract.data!;
  const manage = can(perms, "amc:manage");
  const monthLabel = (ym: string) => new Date(`${ym}-01T00:00:00Z`).toLocaleDateString(displayPrefs().locale, { month: "short", year: "2-digit", timeZone: "UTC" });
  return (
    <>
      <Link to="/amc" className="mb-4 inline-block text-sm text-muted hover:text-brand">← All contracts</Link>
      <PageHeader title={`${c.number}: ${c.account.name}`} subtitle={`${shortDate(c.starts_on)} to ${shortDate(c.ends_on)} · ${money(c.value, c.currency)} · ${c.visits_per_year} visit${c.visits_per_year === 1 ? "" : "s"} a year`}
        actions={manage && c.status === "active" && <>
          {c.renewal_quote_id ? <Link to={`/quotes/${c.renewal_quote_id}`} className="btn-ghost">Renewal quote</Link> : <button type="button" className="btn-ghost" onClick={() => setDialog("quote")}>Send renewal quote</button>}
          <button type="button" className="btn-primary" onClick={() => setDialog("renew")}>Renew now</button>
        </>} />
      <div className="mb-6 flex flex-wrap items-center gap-3 text-sm">
        <Badge tone={AMC_STATUS[c.status][1]}>{AMC_STATUS[c.status][0]}</Badge>
        {c.status === "active" && <span className={c.renewal_due ? "font-medium text-warn" : "text-muted"}>{c.days_left >= 0 ? `Ends in ${c.days_left} days` : "Ended"}</span>}
        {c.renewed_from && <Link className="text-brand hover:underline" to={`/amc/${c.renewed_from}`}>Renewal of an earlier contract</Link>}
        {c.renewed_to && <Link className="text-brand hover:underline" to={`/amc/${c.renewed_to}`}>Renewed: see the new contract</Link>}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="card" aria-label="Machines covered">
          <h2 className="border-b border-line px-5 py-3 font-semibold">Machines covered</h2>
          {c.assets.length ? <ul className="divide-y divide-line">{c.assets.map((a) => <li key={a.id} className="px-5 py-2 text-sm">{a.name}</li>)}</ul> : <p className="px-5 py-4 text-sm text-muted">No machines listed.</p>}
        </section>
        <section className="card" aria-label="Visit plan">
          <h2 className="border-b border-line px-5 py-3 font-semibold">Visit plan</h2>
          {c.visits.length ? <ul className="divide-y divide-line">{c.visits.map((v) => (
            <li key={v.id} className="flex items-center justify-between gap-2 px-5 py-2 text-sm">
              <Link to={`/service/${v.id}`} className="hover:text-brand">{v.number} · {v.scheduled_for ? formatDate(v.scheduled_for) : "not scheduled"}</Link>
              <span className="flex items-center gap-2">{v.assignee && <span className="text-xs text-muted">{v.assignee.full_name}</span>}<StatusBadge s={v.status} /></span>
            </li>))}</ul> : <p className="px-5 py-4 text-sm text-muted">No preventive visits in this contract.</p>}
        </section>
      </div>
      <div className="mt-6">
        <ColumnChart title="Revenue by month" subtitle="The contract value spread over its months" valueHeader="Revenue" ticks={(n) => money(n, c.currency, true)}
          points={c.revenue.map((r) => ({ key: r.month, label: monthLabel(r.month), value: r.amount, display: money(r.amount, c.currency) }))} />
      </div>
      {dialog && <RenewDialog c={c} mode={dialog} onClose={() => setDialog(null)} />}
    </>
  );
}

export { NewContract };
